import { useEffect, useState, useMemo } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { DataForm } from '@design-system/DataForm/DataForm';
import { Button } from '@design-system/Button';
import { FullScreenPageContainer } from '@/components/utils/FullScreenPageContainer';
import { useForm } from 'react-hook-form';
import { CheckboxField } from '@design-system/DataForm/CheckboxField';
import useConfirmRegister from '@/hooks/registration/useConfirmRegister';
import useRegisterStore, {
  RegistrationTrack,
} from '@/hooks/useRegistrationStore';
import CircularLoader from '@/components/utils/CircularLoader';
import useRegisterRouteSafety from '@/hooks/registration/useRegisterRouteSafety';
import { useNavigate } from 'react-router-dom';
import * as P from '@/constants/paths';
import { RegistrationSuccess } from './RegistrationSuccess';

export const RegistrationTermsOfService = () => {
  const { onSubmit, registrationPending } = useConfirmRegister();
  const { majorConsent, minorConsent, acceptMinorConsent, registrationDone } =
    useRegisterStore();
  const { checkTheRouteAndRegisterStore } = useRegisterRouteSafety();
  const navigate = useNavigate();

  const [tosContent, setTosContent] = useState('');
  const tosHtml = majorConsent?.content;

  useEffect(() => {
    if (minorConsent && !acceptMinorConsent) {
      navigate(-1);
    } else {
      if (tosHtml) {
        setTosContent(tosHtml);
      } else {
        checkTheRouteAndRegisterStore(RegistrationTrack.TermsOfService);
      }
    }
  }, [majorConsent]);

  const formSchema = useMemo(() => {
    if (!majorConsent?.choices) return z.object({});

    const shape = majorConsent.choices.reduce(
      (acc, choice) => {
        acc[choice.recordValue] = z.boolean().refine((val) => val === true, {
          message: 'You need to agree to proceed',
        });
        return acc;
      },
      {} as Record<string, z.ZodTypeAny>,
    );

    return z.object(shape).refine(
      (data) => {
        return Object.values(data).every((value) => value === true);
      },
      {
        message: 'You must agree to all terms to proceed',
        path: ['__all__'],
      },
    );
  }, [majorConsent]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: majorConsent?.choices?.reduce(
      (acc, choice) => ({ ...acc, [choice.recordValue]: false }),
      {} as Record<string, boolean>,
    ),
    mode: 'onChange',
  });

  return registrationDone ? (
    <RegistrationSuccess />
  ) : (
    <FullScreenPageContainer
      isLoading={false}
      previousPath={P.REGISTRATION_CONFIRMATION}
    >
      <div className="flex flex-col items-center w-full">
        <div className="w-full mb-4">
          <div
            className="prose max-w-none [&>h1]:typography-d3"
            dangerouslySetInnerHTML={{
              __html: tosContent.split('Introduction')[0],
            }}
          />
        </div>

        <div className="w-full bg-white rounded-xl shadow-sm p-4">
          <div className="max-h-[44vh] overflow-y-auto">
            <div className="p-6 typography-body text-secondary">
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{
                  __html: tosContent.includes('Introduction')
                    ? 'Introduction' + tosContent.split('Introduction')[1]
                    : tosContent,
                }}
              />
            </div>
          </div>
        </div>
        <div className="w-full mt-6">
          <DataForm form={form} onValid={onSubmit}>
            <div className="space-y-4">
              {majorConsent?.choices.map((value) => (
                <CheckboxField
                  key={value.ordinal}
                  name={value.recordValue}
                  label={value.content}
                  disabled={registrationPending}
                />
              ))}
              <Button
                disabled={registrationPending}
                type="submit"
                className="w-80 text-white font-semibold bg-black rounded-lg"
              >
                {registrationPending ? <CircularLoader /> : 'Next'}
              </Button>
            </div>
          </DataForm>
        </div>
      </div>
    </FullScreenPageContainer>
  );
};
